import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getTaskPreferences, updateTaskPreferences } from '@/services/taskPreferences.service';
import { logger } from '@/lib/logger';
import { TaskPreferences, defaultTaskPreferences } from '@/types/taskPreferences';
import { toast } from 'sonner';

/**
 * Hook to get user's task preferences
 */
export function useTaskPreferences() {
  const { user } = useAuth();
  const userId = user?.uid;

  return useQuery({
    queryKey: ['taskPreferences', userId],
    queryFn: () => getTaskPreferences(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: defaultTaskPreferences,
  });
}

/**
 * Hook to update task preferences
 */
export function useUpdateTaskPreferences() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: Partial<TaskPreferences>) =>
      updateTaskPreferences(userId!, preferences),
    onSuccess: () => {
      // Invalidate to refetch
      void void queryClient.invalidateQueries({ queryKey: ['taskPreferences', userId] });
      toast.success(t('toasts.taskPreferences.updated'));
    },
    onError: (error) => {
      logger.error('Failed to update task preferences:', error);
      toast.error(t('toasts.taskPreferences.updateFailed'));
    },
  });
}
